import React, { createRef, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useNavigate } from "react-router";

import useQueryParams from "src/hooks/useQueryParams";
import FieldsSettings from "./FieldsSettingsForm";
import ConfigurationForm from "./ConfigurationForm";
import useAuth from "../../../hooks/useAuth";
import useUnsavedChanges from "../../../hooks/useUnsavedChanges";
import UnsavedChangesDialog from "../../modals/UnsavedChanges/UnsavedChangesDialog";

const styles = {
  tab: {
    textTransform: "none",
    fontSize: 16,
  },
};

const formikRef = createRef();

export default function ConfigurationModal({
  project,
  selectedTab,
  storageEnabled,
  originEnabled,
  destinationEnabled,
  ordersTableFieldsEnabled,
  reservationsTableFieldsEnabled,
  onClose,
}) {
  const { user } = useAuth();
  const params = useQueryParams();
  const navigate = useNavigate();
  const { settings } = user.company;
  const tabIndex = parseInt(selectedTab);
  const showPdfFields = settings.receiving_wizard || settings.inventory_tracking;
  const showReceivingAssistantFields = settings.receiving_wizard && storageEnabled;
  const pendingInventoryAutosaveRef = useRef(null);

  const {
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving,
  } = useUnsavedChanges({
    onClose,
    isDirtyFn: () => !!formikRef.current?.hasUnsavedChanges,
    onSave: async () => {
      const submitFn = formikRef.current?.submitForm;
      submitFn();
    },
  });

  const tabs = [
    { label: "Inventory Fields", value: 1, active: true },
    {
      label: "Receiving Assistant Fields",
      value: 2,
      active: showReceivingAssistantFields,
    },
    { label: "Screen Layout Fields", value: 3, active: true },
    { label: "PDF Fields", value: 4, active: showPdfFields },
  ].filter(tab => tab.active);

  const activeTab = tabs[tabIndex].value;

  const triggerInventoryAutosave = () => {
    const autosaveFn = formikRef.current?.autosaveInventorySettings;

    if (!autosaveFn) return;
    if (pendingInventoryAutosaveRef.current) return pendingInventoryAutosaveRef.current;

    pendingInventoryAutosaveRef.current = Promise.resolve(autosaveFn()).finally(() => {
      pendingInventoryAutosaveRef.current = null;
    });

    return pendingInventoryAutosaveRef.current;
  };

  const maybeAutosaveInventorySettings = nextTabValue => {
    if (activeTab !== 1 || nextTabValue === 1 || !formikRef.current?.hasUnsavedInventoryChanges) {
      return;
    }

    triggerInventoryAutosave();
  };

  const handleTabChange = (event, newValue) => {
    maybeAutosaveInventorySettings(newValue);
    setTabIndex(tabs.findIndex(tab => tab.value === newValue));
  };

  const setTabIndex = newIndex => {
    params.set("configuration", newIndex);
    navigate({ search: params.toString() });
  };

  const handleNext = () => {
    const nextTabIndex = tabs.findIndex(tab => tab.active && tab.value > activeTab);
    if (nextTabIndex !== -1) {
      maybeAutosaveInventorySettings(tabs[nextTabIndex].value);
      setTabIndex(nextTabIndex);
    }
  };

  const handleBack = () => {
    const previousTabIndex = tabs.findLastIndex(tab => tab.active && tab.value < activeTab);
    if (previousTabIndex !== -1) {
      setTabIndex(previousTabIndex);
    }
  };

  return (
    <ConfigurationForm project={project} onClose={onClose} formikRef={formikRef}>
      <Dialog
        open
        maxWidth={false}
        fullWidth={false}
        onClose={attemptClose}
        sx={{ "& .MuiDialog-paper": { width: "800px", maxWidth: "90vw" } }}
      >
        <DialogTitle sx={{ bgcolor: "#f4f6f8", pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box width={40} />
            <Typography color="textSecondary" fontSize={26} fontWeight={500}>
              Field Configuration
            </Typography>
            <IconButton sx={{ mr: 2 }} color="textPrimary" onClick={attemptClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ mt: 2, borderBottom: 1, borderColor: "divider", bgcolor: "#fff" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="configuration tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ textTransform: "none" }}
            >
              {tabs.map(tab => (
                <Tab key={tab.value} label={tab.label} value={tab.value} sx={styles.tab} />
              ))}
            </Tabs>
          </Box>
        </DialogTitle>
        <FieldsSettings
          project={project}
          activeTab={activeTab}
          storageEnabled={storageEnabled}
          originEnabled={originEnabled}
          destinationEnabled={destinationEnabled}
          ordersTableFieldsEnabled={ordersTableFieldsEnabled}
          reservationsTableFieldsEnabled={reservationsTableFieldsEnabled}
          hideTitle={false}
          onClose={onClose}
          onNext={handleNext}
          onBack={handleBack}
          isLastStep={tabIndex === tabs.length - 1}
        />
      </Dialog>
      {showDialog && (
        <UnsavedChangesDialog
          open={showDialog}
          onCancel={cancelDiscard}
          onDiscard={confirmDiscard}
          onSave={confirmSave}
          saving={saving}
        />
      )}
    </ConfigurationForm>
  );
}
